import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label } from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Landmark } from 'lucide-react';
import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { FieldError } from '@/components/ui/field-error';
import { FEE_EXCLUSIONS_QUERY_KEY } from '@/hooks/queries/fees/use-fees';
import { ACCOUNT_HOLDER_ID_LENGTH, addFeeExclusion } from '@/services/api/fees';

// Exactly-36-characters is the whole check (UUID
// length, not a UUID-format regex) — the server does not validate the format at all.
const feeExclusionSchema = z.object({
  pid: z.string().length(ACCOUNT_HOLDER_ID_LENGTH, `Account Holder ID must be exactly ${ACCOUNT_HOLDER_ID_LENGTH} characters`),
  name: z.string().min(1, 'Name is required'),
});

type FeeExclusionSchema = z.infer<typeof feeExclusionSchema>;

const DEFAULT_VALUES: FeeExclusionSchema = { pid: '', name: '' };

interface FeeExclusionDialogProps {
  onClose: () => void;
}

// Mounted only while open — fields clear on every open/close.
export const FeeExclusionDialog = ({ onClose }: FeeExclusionDialogProps) => {
  const fieldId = useId();
  const queryClient = useQueryClient();
  const form = useForm<FeeExclusionSchema>({ resolver: zodResolver(feeExclusionSchema), defaultValues: DEFAULT_VALUES });

  const handleSubmit = form.handleSubmit(async ({ pid, name }) => {
    const justification = await confirmAction({
      title: 'Confirm Exclude user from fees',
      description: `Proceeding with this action will exclude ${pid} (${name}) from deposit and withdrawal fees. Your action here will be logged and will be auditable.`,
      actionLabel: 'Exclude user from fees',
      // Asymmetry: the list returns `pid`; the add sends `accountHolderId`
      onConfirm: (comment) => addFeeExclusion({ accountHolderId: pid, name, comment }),
    });
    if (justification === null) return;

    // addFeeExclusion can 200 even after an internal failure —
    // "submitted" over "added", and refetch so the operator verifies the row.
    toast.success(`Fee exclusion submitted for ${name} — verify it appears in the list.`);
    await queryClient.invalidateQueries({ queryKey: FEE_EXCLUSIONS_QUERY_KEY });
    onClose();
  });

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="size-5" />
            New Fee Exclusion
          </DialogTitle>
        </DialogHeader>

        <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-pid`}>Account Holder ID (PID)</Label>
            <Input id={`${fieldId}-pid`} {...form.register('pid')} />
            <FieldError message={form.formState.errors.pid?.message} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-name`}>Name</Label>
            <Input id={`${fieldId}-name`} {...form.register('name')} />
            <FieldError message={form.formState.errors.name?.message} />
          </div>

          <DialogFooter>
            <Button onClick={onClose} type="button" variant="outline">
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Add
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
FeeExclusionDialog.displayName = 'FeeExclusionDialog';
